import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  Divider,
  Icon,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
// eslint-disable-next-line import/named
import { Link as RouterLink, RouteComponentProps } from 'react-router';

import { Page } from '../../components/layouts';
import { CollectionIcon, DeliveryIcon } from '../../ui/Icon/AppIcons';
import * as Api from '../../weborder/Api';
import { ToCurrency } from '../../weborder/BasketControl';
import LoadingPanel from '../../weborder/component/LoadingPanel';
import { DispatchProps, StateProps } from '../../weborder/component/OrderView';
import { orderApiServices, storageServices } from '../../weborder/services';
import { CountDown } from './components';

const useStyles = makeStyles((theme: Theme) => ({
  address: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  icons: {
    marginRight: '3px',
    width: '20px',
  },
  list: {
    width: '80%',

    [theme.breakpoints.up('sm')]: {
      width: '400px',
    },
  },
  listItem: {
    alignItems: 'flex-start',
    paddingLeft: 0,
    paddingRight: 0,
  },
  totalListItem: {
    fontWeight: 'bold',
  },
  price: {
    textAlign: 'right',
  },
}));

const ORDER_CANCELLED_STATES = [
  Api.OrderState.Cancelled,
  Api.OrderState.RejectedByRestaurant,
  Api.OrderState.RejectedByFlipdish,
  Api.OrderState.RejectedAutomatically,
  Api.OrderState.RejectedAfterBeingAccepted,
];

type RouterParams = RouteComponentProps<
  {
    orderId: string;
    concessionStoreId?: string;
  },
  {}
>;

type Props = StateProps & DispatchProps & RouterParams;

export function OrderComplete(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    basketSnapshot,
    cancelCheckoutFlow,
    cancelTimeout,
    ClearBasket,
    continueCheckoutFlow,
    params,
    setDefaultPayment,
    translate,
  } = props;

  const { orderId, concessionStoreId } = params;

  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<
    Api.PreviousOrderDm & { isCancelled: boolean }
  >();

  // if the flag is active and an error response has returned from the backend.
  const redirectToCheckoutIsActive = order && order?.PaymentProcessingResponse;

  useEffect(() => {
    return () => {
      if (!isCancelFlag.current) {
        continueCheckoutFlow();
      }
    };
  }, []);

  useEffect(() => {
    if (redirectToCheckoutIsActive) {
      redirectToCheckoutOnPaymentError();
    }
  }, [redirectToCheckoutIsActive]);

  useEffect(() => {
    if (orderId) {
      setLoading(true);

      orderApiServices
        .get({ orderId })
        .then((o) => {
          const orderCancelled = ORDER_CANCELLED_STATES.includes(o.OrderState);

          // If accepted order from redirect clear basket
          // eslint-disable react-hooks/exhaustive-deps
          if (!cancelTimeout && !orderCancelled) {
            const meta = storageServices.getSubmittedOrderMeta(orderId);
            const menuId = (meta && meta.menuId) || undefined;
            const paymentId = meta && meta.paymentId;

            if (paymentId) {
              setDefaultPayment({ paymentId });
            }

            // eslint-disable react-hooks/exhaustive-deps
            ClearBasket({
              menuId,
            });
          }

          setOrder({ ...o, isCancelled: orderCancelled });
          setError(undefined);
          setLoading(false);
        })
        .catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            console.error(error);
          }
          setError(new Error('Order_loading_error'));
          setLoading(false);
        });
    }
  }, [orderId]);

  const getBasketSummary = () => {
    const summary = Object.values(basketSnapshot.basketItems).map(
      (item: any) => {
        let options = undefined;
        if (item.item?.Options) {
          options = Object.values(item.item.Options).map(
            (option: any) => option.Name,
          );
        }
        return { options: options, item };
      },
    );

    let voucherAmount = 0;
    let voucherCode = '';
    let voucherDescription = '';
    if (basketSnapshot.voucher.code !== '') {
      voucherCode = basketSnapshot.voucher.code;
      voucherDescription = basketSnapshot.voucher.items.map(
        (item) => item.Description,
      );
      voucherAmount = basketSnapshot.voucher.items.map((item) => item.Amount);
    }

    return {
      currencyNumber: basketSnapshot.total?.currency,
      deliveryFee: basketSnapshot.deliveryFee,
      displayTax: basketSnapshot.displayTax,
      initialLang: basketSnapshot.initialLang,
      paymentType: basketSnapshot.paymentType,
      percentageTip: basketSnapshot.percentageTip,
      physicalRestaurantAddress: basketSnapshot.physicalRestaurantAddress,
      processingFee: basketSnapshot.processingFee,
      processingFeeTaxRate: basketSnapshot.processingFeeTaxRate,
      taxes: basketSnapshot.taxes,
      tipAmount: basketSnapshot.total.order.TipAmount,
      totalAmount: basketSnapshot.total.totalToPay,
      summary,
      voucher: {
        voucherAmount,
        voucherCode,
        voucherDescription,
      },
    };
  };

  let basketSummary: any = false;
  if (basketSnapshot !== undefined) {
    basketSummary = getBasketSummary();
  }

  const isCancelFlag = useRef(false);

  const getCheckoutUrl = () => {
    const deliveryType =
      order.DeliveryType === Api.DeliveryType.Collection
        ? 'collection'
        : 'delivery';
    const errorParam = `?paymentError=${order.PaymentProcessingResponse}`;
    const windowLocationHref = window.location.href;
    const splitURLOnQuery = windowLocationHref.includes('#/order/')
      ? windowLocationHref.split('#/order/')
      : windowLocationHref.split('/order?');
    const vrId = order.VirtualRestaurantId;

    let checkoutRoute = `/order/#/checkout/${vrId}/${deliveryType}/${order.PhysicalRestaurantId}`;
    // add concessionStoreId if present
    if (concessionStoreId) {
      checkoutRoute = checkoutRoute + `/${concessionStoreId}`;
    }
    checkoutRoute = checkoutRoute + errorParam;

    return splitURLOnQuery[0] + checkoutRoute;
  };

  const redirectToCheckoutOnPaymentError = () => {
    const checkoutURL = getCheckoutUrl();
    window.location.href = checkoutURL;
  };

  const renderMenuLink = useMemo(() => {
    let menuRoute = '/';
    if (order) {
      const vrId = order.VirtualRestaurantId;
      if (order.DeliveryType === Api.DeliveryType.Collection) {
        menuRoute = `/restaurant/${vrId}/collection/${order.PhysicalRestaurantId}`;
      } else {
        menuRoute = `/restaurant/${vrId}/delivery/${order.DeliveryLocationId}`;
      }
      if (concessionStoreId) {
        menuRoute = menuRoute + `/${concessionStoreId}`;
      }
    }

    return React.forwardRef((props, ref) => (
      <RouterLink ref={ref as any} {...props} to={menuRoute} />
    ));
  }, [order, concessionStoreId]);

  const handleCancelOrder = () => {
    isCancelFlag.current = true;
    cancelCheckoutFlow(); // will cancel order
  };

  const renderOrderSummaryLine = (
    label,
    value,
    subLabel = null,
    className = null,
  ) => {
    const listItemStyle = clsx(classes.listItem, className && className);

    return (
      <>
        <ListItem className={listItemStyle}>
          <ListItemText
            primary={
              <Typography component="span" variant="h6">
                {label}
              </Typography>
            }
            secondary={
              <Typography component="span" variant="caption">
                {subLabel}
              </Typography>
            }
          />
          <ListItemText
            className={classes.price}
            primary={
              <Typography component="span" variant="h6">
                {value}
              </Typography>
            }
          />
        </ListItem>

        <Divider component="li" />
      </>
    );
  };

  if (error) {
    return (
      <Page pageName="order-confirmation" noLayout>
        <Paper square>
          <Typography variant="h4">
            {translate(error.message, undefined, {
              missingTranslationMsg: error.message,
            })}
          </Typography>
        </Paper>
      </Page>
    );
  }

  if (loading || redirectToCheckoutIsActive) {
    return <LoadingPanel />;
  }

  const { currencyNumber: currency, initialLang: lang } = basketSummary;

  return (
    <Page pageName="order-complete" noLayout>
      <Paper square>
        <Typography variant="h1">
          {order.isCancelled ? translate('Cancelled') : translate('Thank_you')}
        </Typography>

        <Icon
          className={`${
            order.isCancelled ? 'fa-times-circle' : 'fa-check-circle'
          }`}
          aria-hidden="true"
        />

        <Typography variant="h2">
          {order.isCancelled
            ? translate('This_order_has_been_cancelled')
            : translate('Your_order_has_been_placed')}
        </Typography>

        <Typography variant="h2">{order.RestaurantName}</Typography>

        {cancelTimeout > 0 ? (
          <CountDown
            onCancel={handleCancelOrder}
            text={translate('Cancel_order') as string}
            timeout={cancelTimeout}
          />
        ) : null}

        {basketSummary !== false ? (
          <List className={classes.list}>
            <ListItem className={classes.address}>
              {order.DeliveryType === Api.DeliveryType.Delivery ? (
                <DeliveryIcon className={classes.icons} />
              ) : (
                <CollectionIcon className={classes.icons} />
              )}
              <ListItemText>
                <Typography component="span" variant="h6">
                  {order.DeliveryType === Api.DeliveryType.Delivery
                    ? translate('Delivering_to_order', {
                        address: order.DeliveryLocationAddressString,
                      })
                    : `${translate('Collect_from')} ${
                        basketSummary.physicalRestaurantAddress
                      }`}
                </Typography>
              </ListItemText>
            </ListItem>

            {basketSummary.summary.map((item, index) => (
              <>
                <ListItem key={index} className={classes.listItem}>
                  <ListItemText
                    primary={
                      <Typography key={index} component="span" variant="h6">
                        {item.item.occurence} x {item.item.item.Item.Name}
                      </Typography>
                    }
                    secondary={item.options.map((itemOptions, index) => (
                      <Typography
                        key={index}
                        component="span"
                        variant="caption"
                      >
                        {itemOptions}
                        <br />
                      </Typography>
                    ))}
                  />
                  <ListItemText
                    className={classes.price}
                    primary={
                      <Typography component="span" variant="h6">
                        {ToCurrency(
                          basketSnapshot.basketItemPrices[index] *
                            item.item.occurence,
                          basketSummary.currencyNumber,
                          basketSummary.initialLang,
                        )}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </>
            ))}

            {basketSummary.deliveryFee &&
              renderOrderSummaryLine(
                translate('Delivery_fee'),
                ToCurrency(basketSummary.deliveryFee, currency, lang),
              )}

            {basketSummary.processingFee > 0 &&
              renderOrderSummaryLine(
                translate('Online_ordering_fee'),
                ToCurrency(basketSummary.processingFee, currency, lang),
                basketSummary.displayTax
                  ? translate('Includes_tax_of', {
                      tax: ` ${basketSummary.processingFeeTaxRate}%`,
                    })
                  : null,
              )}

            {basketSummary.taxes &&
              renderOrderSummaryLine(
                translate('Tax'),
                ToCurrency(basketSummary.taxes, currency, lang),
              )}

            {basketSummary.tipAmount &&
              basketSummary.percentageTip > 0 &&
              renderOrderSummaryLine(
                `${translate('Tip')} ${basketSummary.percentageTip}%`,
                ToCurrency(basketSummary.tipAmount, currency, lang),
              )}

            {basketSummary.voucher.voucherCode !== '' &&
              renderOrderSummaryLine(
                basketSummary.voucher.voucherCode,
                ToCurrency(basketSummary.voucher.voucherAmount, currency, lang),
                basketSummary.voucher.voucherDescription,
              )}

            {renderOrderSummaryLine(
              translate('Total'),
              ToCurrency(basketSummary.totalAmount, currency, lang),
              basketSummary.paymentType,
              classes.totalListItem,
            )}
          </List>
        ) : null}

        <Button component={renderMenuLink} disabled={loading} size="large">
          {translate('Back_to_menu')}
        </Button>

        <Link href="https://www.flipdish.com/" target="_blank">
          {translate('Powered_by_flipdish')}
        </Link>
      </Paper>
    </Page>
  );
}
